'use client'
import { useMemo } from 'react'
import { axios } from '@/configs'
import useSWR from 'swr/immutable'
import { useSWRConfig } from 'swr'

interface ApiUserDetailsResponse {
  userId?: number
  firstName?: string
  lastName?: string
  email?: string
  phone?: {
    intlCode?: string
    phoneNumber?: string
  }
  userName?: string
  status?: string
  registrationMethod?: string
  enabled?: boolean
  createdAt?: string
  roles?: Array<{
    code?: string
    title?: string
    permissions?: string[]
  }>
}

interface ApiAccount {
  accountId?: number
  username?: string
  registrationMethod?: string
}

interface ApiPhone {
  intlCode?: string
  phoneNumber?: string
}

interface ApiCompanyInMemberOf {
  companyId?: number
  legalName?: string
  registrationNumber?: string
  country?: string
  city?: string
  zip?: string
  addressLine?: string
  monthlyCreditLimit?: number
  monthlyCreditLimitBalance?: number
  monthlyCreditLimitExpireInDays?: number
  creditCompany?: boolean
}

interface ApiMemberOf {
  company?: ApiCompanyInMemberOf
  manager?: boolean
  enabled?: boolean
}

interface ApiCustomerResponse {
  customerId?: number
  firstName?: string
  lastName?: string
  email?: string
  phone?: ApiPhone
  accounts?: ApiAccount[]
  memberOf?: ApiMemberOf[]
  personalIdentificationNumber?: string
  status?: string
  createdAt?: string
  updatedAt?: string
  createdBy?: string
  createdByCustomerId?: number
  userIds?: number[]
}

interface TransformedUser {
  email: string
  firstName: string
  lastName: string
  phone: string
  type: 'private' | 'legal'
  registrationMethod: string
}

interface TransformedCompany {
  companyName: string
  regNumber: string
  creditLimit: number
  used: number
  daysUntilRenew: number
  city?: string
  country?: string
  legalAddress?: string
  zip?: string
  enabled?: boolean
  manager?: boolean
  companyId?: number
  creditCompany?: boolean
}

const fetchUserDetails = async (): Promise<ApiUserDetailsResponse | null> => {
  const response = await axios.get<ApiUserDetailsResponse>('user/details')
  return response.data ?? null
}

const fetchCustomerData = async (): Promise<ApiCustomerResponse | null> => {
  const response = await axios.put<ApiCustomerResponse>(
    'user/customer',
    {},
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )
  return response.data ?? null
}

const formatPhone = (phone?: ApiPhone): string => {
  if (!phone) return ''
  return `${phone.intlCode ?? ''} ${phone.phoneNumber ?? ''}`.trim()
}

export const useGetProfileData = (isAuthenticated = true) => {
  const { mutate: globalMutate } = useSWRConfig()

  const {
    data: userDetails,
    error: userDetailsError,
    isLoading: isLoadingUserDetails,
  } = useSWR(isAuthenticated ? 'profile-user-details' : null, fetchUserDetails)

  const {
    data: customerData,
    error: customerError,
    isLoading: isLoadingCustomer,
  } = useSWR(isAuthenticated ? 'profile-customer' : null, fetchCustomerData)

  const isLoading = isLoadingUserDetails || isLoadingCustomer
  const error = userDetailsError || customerError

  const user: TransformedUser | null = useMemo(() => {
    const firstName = customerData?.firstName ?? userDetails?.firstName ?? ''
    const lastName = customerData?.lastName ?? userDetails?.lastName ?? ''
    const email = customerData?.email ?? userDetails?.email ?? ''
    const phone = formatPhone(customerData?.phone) || formatPhone(userDetails?.phone)
    const type = customerData?.memberOf && customerData.memberOf.length > 0 ? 'legal' : 'private'

    return { firstName, lastName, email, phone, type, registrationMethod: userDetails?.registrationMethod ?? '' }
  }, [customerData, userDetails])

  const companies: TransformedCompany[] = useMemo(() => {
    const memberOfArray = customerData?.memberOf ?? []

    return memberOfArray.map((memberOf) => {
      const company = memberOf.company ?? {}

      return {
        companyName: company.legalName ?? '',
        regNumber: company.registrationNumber ?? '',
        creditLimit: company.monthlyCreditLimit ?? 0,
        used: (company.monthlyCreditLimit ?? 0) - (company.monthlyCreditLimitBalance ?? 0),
        daysUntilRenew: company.monthlyCreditLimitExpireInDays ?? 0,
        city: company.city,
        country: company.country,
        legalAddress: company.addressLine,
        zip: company.zip,
        enabled: memberOf.enabled,
        manager: memberOf.manager,
        companyId: company.companyId,
        creditCompany: company.creditCompany ?? false,
      }
    })
  }, [customerData?.memberOf])

  const mutateAll = async () => {
    await Promise.all([globalMutate('profile-user-details'), globalMutate('profile-customer')])
  }

  const userId = customerData?.accounts?.[0]?.accountId ?? userDetails?.userId

  return {
    user,
    companies,
    customerId: customerData?.customerId,
    userId,
    isLoading,
    error,
    mutateUser: mutateAll,
    mutateCompanies: mutateAll,
  }
}
